// QuickActionsFab — presentational floating "+" button with an upward menu.
// Generic: the feature layer supplies the items (label, caption, icon, enabled
// state + lock message); this component owns only placement and menu behavior
// (outside-click / Esc close, keyboard navigation, ARIA menu semantics).
import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '@/foundation/icons';

export interface QuickActionItem {
  id: string;
  label: string;
  /** One-line description under the label. */
  caption?: string;
  icon: IconName;
  /** Disabled items render with a lock and this tooltip (capability gating). */
  lockMsg?: string;
  onSelect: () => void;
}

export function QuickActionsFab({ items }: { items: QuickActionItem[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Outside-click + Esc close (same pattern as foundation Popover).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div
      ref={wrapRef}
      // Docked above the iOS home indicator / browser chrome so the FAB stops
      // sitting on top of the last table row's actions on a phone (Wave E).
      style={{
        position: 'fixed',
        right: 'var(--space-5)',
        bottom: 'calc(var(--space-5) + env(safe-area-inset-bottom))',
        zIndex: 50,
      }}
      data-testid="quick-actions-fab"
    >
      {open && (
        <div
          role="menu"
          aria-label="Quick actions"
          style={{
            position: 'absolute',
            right: 0,
            bottom: 'calc(100% + 10px)',
            width: 300,
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--divider)',
            boxShadow: 'var(--shadow-dropdown)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {items.map((item) => {
            const locked = !!item.lockMsg;
            return (
              <span key={item.id} className={locked ? 'gb-wrap' : undefined}>
                <button
                  type="button"
                  role="menuitem"
                  aria-disabled={locked}
                  disabled={locked}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    background: 'transparent',
                    cursor: locked ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    opacity: locked ? 0.55 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!locked) e.currentTarget.style.background = 'var(--grey-200)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Icon
                    name={locked ? 'lock' : item.icon}
                    size={18}
                    color={locked ? 'var(--text-disabled)' : 'var(--primary-main)'}
                    style={{ marginTop: 1, flexShrink: 0 }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--fs-body)',
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {item.label}
                    </span>
                    {item.caption && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--fs-caption)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {item.caption}
                      </span>
                    )}
                  </span>
                </button>
                {locked && <span className="gb-tip">{item.lockMsg}</span>}
              </span>
            );
          })}
        </div>
      )}
      <button
        type="button"
        aria-label="Quick actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: 'var(--primary-main)',
          color: 'var(--primary-contrast)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--shadow-button)',
          transform: open ? 'rotate(45deg)' : 'none',
          transition: 'transform var(--duration-fast) var(--ease)',
        }}
      >
        <Icon name="plus" size={26} color="currentColor" />
      </button>
    </div>
  );
}
